import { useState, useEffect, useRef } from 'react';
import { MdOutlineAdd, MdClose } from 'react-icons/md';
import { BsPuzzle } from 'react-icons/bs';
import toast from 'react-hot-toast';

import Spinner from './Spinner';

const GlobalConnectionItem = ({
  globalConn,
  onInitiateOauth,
  onSaveCustom,
}) => {
  // State for the form that appears for custom headers
  const [isExpanded, setIsExpanded] = useState(false);
  // State to hold user input for each required header
  const [headerValues, setHeaderValues] = useState({});
  // State to show a loading spinner on the button
  const [isConnecting, setIsConnecting] = useState(false);

  // Check if the connection is custom and has headers that the user needs to fill in
  const hasRequiredHeaders =
    globalConn.auth_type === 'custom_header' &&
    globalConn.required_headers?.length > 0;

  // Update state as the user types in the header fields
  const handleInputChange = (headerName, value) => {
    setHeaderValues((prev) => ({ ...prev, [headerName]: value }));
  };

  const handleConnect = async () => {
    // 1. If we need user input, just expand the form and wait.
    if (hasRequiredHeaders && !isExpanded) {
      setIsExpanded(true);
      return;
    }

    // 2. If we are ready to connect, show the spinner.
    setIsConnecting(true);
    try {
      if (globalConn.auth_type === 'oauth2') {
        // For OAuth, connect immediately.
        await onInitiateOauth({
          connection_name: globalConn.origin_name, // Use the default name
          sse_url: globalConn.sse_url,
          origin: globalConn.origin,
        });
      } else if (globalConn.auth_type === 'custom_header') {
        // For custom headers, build the headers object from user input.
        // If there were no required headers, this will be an empty object.
        const headers = headerValues;

        // Optional: A simple check to ensure all fields are filled
        if (hasRequiredHeaders) {
          for (const key of globalConn.required_headers) {
            if (!headers[key]) {
              toast.error(`Please fill in the '${key}' field.`);
              setIsConnecting(false);
              return;
            }
          }
        }

        await onSaveCustom({
          connection_name: globalConn.origin_name, // Use the default name
          sse_url: globalConn.sse_url,
          headers: headers,
        });
        setIsExpanded(false); // Close the form on success
      }
    } catch (error) {
      toast.error('Failed to connect. Please try again.');
    } finally {
      // Always stop the spinner
      setIsConnecting(false);
    }
  };

  const modalRef = useRef();

  // Close modal on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  return (
    <div
      key={`global-conn-${globalConn.id}`}
      className="flex w-fit max-w-xl flex-wrap items-center gap-2 rounded-full border border-zinc-400 bg-white p-1  px-2 transition-all duration-300 dark:bg-zinc-800"
      title={globalConn.description}
    >
      {/* Logo */}

      <div className="cursor-pointer rounded-full bg-zinc-200 p-1">
        {globalConn.logo_url ? (
          <img
            src={globalConn.logo_url}
            alt={`${globalConn.origin_name} logo`}
            className="h-5 w-5 object-contain"
          />
        ) : (
          <BsPuzzle className="h-4 w-4 text-zinc-700" />
        )}
      </div>

      {/* App Name & Description */}
      <div className="ml-1 flex flex-col justify-center text-sm text-zinc-800 dark:text-gray-200">
        <span className="max-w-[140px] truncate font-semibold">
          {globalConn.origin_name}
        </span>
        {globalConn.description && (
          <span className="max-w-[160px] truncate text-xs text-gray-500 dark:text-gray-400">
            {globalConn.description}
          </span>
        )}
      </div>

      <button
        onClick={handleConnect}
        disabled={isConnecting}
        className="ml-4 rounded-full bg-zinc-100 p-2 text-xs font-bold text-zinc-700 transition-all duration-300 hover:bg-zinc-300"
      >
        {isConnecting ? <Spinner size="1rem" /> : <MdOutlineAdd size={10} />}
      </button>

      {/* Modal */}
      {hasRequiredHeaders && isExpanded && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            ref={modalRef}
            className="relative w-full max-w-lg space-y-4 rounded-lg border border-zinc-700 bg-zinc-800 p-6 shadow-lg"
          >
            {/* Close Button */}
            <button
              onClick={() => setIsExpanded(false)}
              className="absolute right-3 top-3 text-white hover:text-red-400"
            >
              <MdClose size={20} />
            </button>

            {/* Modal Header */}
            <h2 className="text-lg font-semibold text-white">
              Connect to {globalConn.origin_name}
            </h2>

            <p className="text-sm text-gray-300">
              Please provide the following headers to connect:
            </p>

            {/* Header Inputs */}
            {globalConn.required_headers.map((headerName) => (
              <div key={headerName}>
                <label
                  htmlFor={`header-${headerName}`}
                  className="text-sm font-medium text-gray-300"
                >
                  {headerName}
                </label>
                <input
                  id={`header-${headerName}`}
                  type="text"
                  value={headerValues[headerName] || ''}
                  onChange={(e) =>
                    handleInputChange(headerName, e.target.value)
                  }
                  className="mt-1 w-full rounded-md border border-zinc-600 bg-zinc-900 px-3 py-2 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
                  placeholder={`Enter your ${headerName}`}
                />
              </div>
            ))}

            {/* Modal Actions */}
            <div className="flex justify-end gap-2 pt-4">
              <button
                onClick={handleConnect}
                disabled={isConnecting}
                className="flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 font-semibold text-white hover:bg-green-700 disabled:opacity-50"
              >
                {isConnecting && <Spinner size="1.2rem" />}
                Save Connection
              </button>
              <button
                onClick={() => setIsExpanded(false)}
                className="rounded-md bg-zinc-600 px-4 py-2 font-semibold text-white hover:bg-zinc-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalConnectionItem;
